import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { createClientCredentialApiV1ClientCredentialPost } from "@/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { clientCredentialPermissionSchema, getAvailablePermissionsFromLocalStorage } from "@/lib/utils";
import { API_DOCS_URL } from "@/routes/reset-password-success";
import { ClientCredentialCreatedDialog } from "./ClientCredentialCreatedDialog";

const clientCredentialSchema = z.object({
  scope: clientCredentialPermissionSchema.array().min(1, { message: "Please select at least one permission" }),
});

type ClientCredentialFormValues = z.infer<typeof clientCredentialSchema>;

export function CreateClientCredentialForm() {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const availablePermissions = getAvailablePermissionsFromLocalStorage().filter((p) => p.id !== "pricesheet:read");

  const form = useForm<ClientCredentialFormValues>({
    resolver: zodResolver(clientCredentialSchema),
    defaultValues: {
      scope: availablePermissions.map((p) => p.id),
    },
  });

  const { mutateAsync: createClientCredential, isPending } = useMutation({
    mutationFn: createClientCredentialApiV1ClientCredentialPost,
  });

  async function onSubmit(data: ClientCredentialFormValues) {
    try {
      const response = await createClientCredential({
        requestBody: {
          scope: data.scope,
        },
      });
      setClientSecret(response.client_secret);
      setClientId(response.client_id);
      setDialogOpen(true);
    } catch (error) {
      toast({
        title: t("Error"),
        description: t("Failed to create client credential"),
        variant: "destructive",
      });
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false);
    setClientSecret(null);
    setClientId(null);
    form.reset();
    queryClient.invalidateQueries({ queryKey: ["client-credentials"] });
    navigate({ to: "/client-credentials" });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("Create Client Credential")}</CardTitle>
          <CardDescription>
            {t(
              "Generate a new client credential with selected scopes. The Client ID and Client Secret will be displayed only once after creation. Use these credentials with the POST /api/v1/login/access-token endpoint to obtain an access token that grant secure, authorized access to the API.",
            )}
          </CardDescription>
          <Link to={API_DOCS_URL} target="_blank" className="text-xs text-muted-foreground">
            {t("API Documentation")}: <span className="underline">{API_DOCS_URL}</span>
          </Link>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="scope"
                render={() => (
                  <FormItem>
                    <FormLabel>{t("Permissions")}</FormLabel>
                    {availablePermissions.map((permission) => (
                      <FormField
                        key={permission.id}
                        control={form.control}
                        name="scope"
                        render={({ field }) => {
                          return (
                            <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(permission.id)}
                                  onCheckedChange={(checked) => {
                                    const currentPermissions = field.value || [];
                                    return checked
                                      ? field.onChange([...currentPermissions, permission.id])
                                      : field.onChange(currentPermissions?.filter((value) => value !== permission.id));
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">{permission.label}</FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="min-w-52" disabled={isPending}>
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : t("Create Credential")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <ClientCredentialCreatedDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientId={clientId}
        clientSecret={clientSecret}
        onClose={handleDialogClose}
      />
    </>
  );
}
